import { useEffect, useMemo, useState } from 'react';
import { createFileRoute, Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { SlidersHorizontal, CalendarX2 } from 'lucide-react';
import type { SearchResult } from '@/api/generated';
import { ApartmentCard } from '@/components/ApartmentCard';
import { DateRangePicker, type DateRangeValue } from '@/components/DateRangePicker';
import { GuestPicker } from '@/components/GuestPicker';
import { Select } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Eyebrow } from '@/components/ui/eyebrow';
import { searchQuery, formatAmenity, HttpError } from '@/lib/apartments';
import { captureSearchResultsViewed } from '@/lib/analytics';
import { nightsBetween } from '@/lib/booking';
import { applySeo } from '@/lib/seo';
import { useIsMobile } from '@/lib/useMediaQuery';
import {
  partyFromSearch,
  partyToSearch,
  normalizedPartyFromSearch,
  isPartyComplete,
  type PartyValue,
  type PartySearchParams,
} from '@/lib/party';
import { m } from '@/paraglide/messages';

type Sort = 'recommended' | 'price-low' | 'price-high';

// This grid's `<main max-w-6xl px-6>` also holds a `w-56` (224px) desktop
// filter rail plus the `gap-9` (36px) next to it — 260px the home grid
// doesn't spend — before the grid-cols-3 gap-6 tiling below divides up
// what's left. Rendered when the rail is showing (lg breakpoint, most
// searches — some amenities to filter on); the rare case of a result set
// with no amenities loses the rail and cards render slightly wider than
// this declares, which only costs a marginally softer image, not wasted
// bytes.
//   >=1152px (container capped): (1152 - 48 - 260 - 48) / 3 = 796 / 3
//   1024–1151px (container == 100vw): (100vw - 48 - 260 - 48) / 3
//   640–1023px (2-up, no rail — lg-only): same as the home grid
//   <640px (1-up): same as the home grid
const CARD_SIZES =
  '(min-width: 1152px) calc(796px / 3), (min-width: 1024px) calc((100vw - 356px) / 3), (min-width: 640px) calc((100vw - 72px) / 2), calc(100vw - 48px)';

// Narrows an unknown query-string value into what partyFromSearch (Task 12,
// reviewed — lib/party.ts) accepts. A `child_age` repeated once per child
// decodes to a real array; a SINGLE `child_age` decodes to a lone
// number/string (see qss.js's decode — one occurrence never gets wrapped);
// partyFromSearch itself already handles both shapes, so this only strips
// anything that's neither (e.g. a hand-edited object literal).
function numberish(v: unknown): number | string | undefined {
  return typeof v === 'number' || typeof v === 'string' ? v : undefined;
}
function childAgeish(v: unknown): number | string | (number | string)[] | undefined {
  if (Array.isArray(v)) {
    return v.filter((x): x is number | string => typeof x === 'number' || typeof x === 'string');
  }
  return numberish(v);
}

// A stranger can hand-edit any of these params (adults=0, child_age=99,
// infants=-1) straight into the URL — the boundary must normalize rather
// than let a malformed link put the page in a state the guest can't get out
// of (same reasoning as apartment.$id.tsx's isValidIsoDate guard).
// partyFromSearch already floors adults at 1; this additionally clamps what
// it deliberately leaves alone: an out-of-range child age (the server 422s
// on anything outside 2-17, and this route renders a 422 as "those dates
// don't work" — the wrong copy for a guests problem) and a negative infant
// count.
export const Route = createFileRoute('/search')({
  validateSearch: (
    s: Record<string, unknown>,
  ): { checkin?: string; checkout?: string; guests?: number } & PartySearchParams => {
    const checkin = typeof s.checkin === 'string' ? s.checkin : undefined;
    const checkout = typeof s.checkout === 'string' ? s.checkout : undefined;
    // Every party key is named explicitly, INCLUDING the ones that clamped to
    // nothing. TanStack Router merges the raw search over this result rather
    // than replacing it, so a key we merely omit keeps whatever the URL said:
    // `?child_age=99` survived normalization, reached the server, and came
    // back a 422 that this page renders as "those dates don't work" — the
    // wrong story entirely. `undefined` is present-and-empty and does
    // override; an absent key does not. Same for the deprecated `guests`
    // scalar, which is folded into `adults` and must not linger beside it.
    const party = partyToSearch(
      normalizedPartyFromSearch({
        adults: numberish(s.adults),
        child_age: childAgeish(s.child_age),
        infants: numberish(s.infants),
        guests: numberish(s.guests),
      }),
    );
    return {
      checkin,
      checkout,
      guests: undefined,
      adults: party.adults,
      child_age: party.child_age,
      infants: party.infants,
    };
  },
  component: SearchPage,
});

 
export function SearchPage() {
  const search = useSearch({ strict: false }) as { checkin?: string; checkout?: string } & PartySearchParams;
  const { checkin, checkout } = search;

  // noindex: dated search URLs aren't worth indexing.
  useEffect(() => {
    return applySeo({ title: 'Search — ollio', noindex: true });
  }, []);

  const hasDates = !!checkin && !!checkout;

  if (!hasDates) {
    return (
      <main className="mx-auto w-full max-w-xl flex-1 px-6 py-20 text-center">
        <Eyebrow className="mb-2">Find your stay</Eyebrow>
        <h1 className="font-serif text-3xl font-semibold tracking-tight text-[var(--ollio-ink)]">
          Pick your dates to search
        </h1>
        <p className="mt-2 text-muted">
          Choose your check-in and check-out on the home page to see what's available.
        </p>
        <Link
          to="/"
          className="mt-6 inline-flex h-10 items-center justify-center rounded-full bg-accent px-6 font-semibold text-accent-foreground shadow-[var(--shadow-gold)] transition hover:bg-[var(--accent-hover)]"
        >
          Start your search
        </Link>
      </main>
    );
  }

  return <Results checkin={checkin} checkout={checkout} party={partyFromSearch(search)} />;
}


export function Results({
  checkin,
  checkout,
  party,
}: {
  checkin: string;
  checkout: string;
  party: PartyValue;
}) {
  const navigate = useNavigate();
  const { data, isPending, isError, error } = useQuery(searchQuery({ checkin, checkout, party }));

  // OH-71: results_viewed carries result_count on purpose — it is what tells
  // "nobody searches" apart from "we search and return nothing", and only the
  // second one is a supply problem (OH-74). Keyed on the query itself so a
  // re-render does not re-report the same result set.
  const resultCount = data?.length ?? null;
  useEffect(() => {
    if (resultCount === null) return;
    captureSearchResultsViewed(resultCount, nightsBetween(new Date(checkin), new Date(checkout)));
  }, [resultCount, checkin, checkout]);

  // OH-132: a newly-added child slot starts with NO age (GuestPicker.addChild
  // — an unanswered question stays unanswered, never guessed). Writing every
  // onChange straight to the URL, as this used to, is fatal for that slot
  // specifically: partyToSearch (correctly) omits an age-less child because
  // the server's `child_age` param is a list of ages, not a count — there is
  // no wire form for "a child, age unknown". So the slot vanished from the
  // very next `search` prop this component received, the age <Select> that
  // setChildAge needs never rendered, and the guest had no way to say a
  // child was coming at all. The party now lives here instead and only
  // reaches the URL once isPartyComplete says every slot has an answer —
  // unlike pendingRange below, this isn't about avoiding an unmount (a
  // partial party doesn't touch `hasDates`), it's that a partial party
  // simply has no representation to write.
  const [pendingParty, setPendingParty] = useState<PartyValue>(party);

  // SearchPage calls partyFromSearch(search) fresh on every render, so the
  // `party` prop is a brand-new object each time even when the URL's party
  // didn't change — a value key, not the object itself, is what the resync
  // effect below should react to. A delimited string, not JSON.stringify:
  // review round 1 (OH-132) flagged stringify's output as key-ORDER-
  // sensitive (a harmless-looking reorder of partyFromSearch's return
  // literal would silently change the key), which this sidesteps by naming
  // each field explicitly instead of relying on object-literal order.
  // `childrenAges.join(',')` collides on `[null]` vs `[]` (both join to
  // `''`) — harmless here because `party` is always what partyFromSearch
  // returns, which never yields a null age (only the picker's own addChild
  // does, per lib/party.ts), so a `[null]` can never actually reach this key.
  const partyKey = `${party.adults}|${party.childrenAges.join(',')}|${party.infants}`;

  // Resync on any URL-driven party change we didn't cause via
  // handlePartyChange below: browser back/forward, a shared link, or a
  // second tab all change `party` without going through that function.
  // handleRangeChange below is NOT one of those cases, despite also calling
  // navigate(): it re-navigates carrying the last COMPLETE `party` prop (not
  // pendingParty) — which is exactly what the URL's party already says — so
  // partyKey comes out unchanged and this effect does not fire. A guest
  // mid-way through answering a child's age who then changes dates keeps
  // that unanswered slot; a date-only navigation deliberately does not
  // reset it (review round 1, OH-132 — the earlier wording here claimed the
  // opposite and was wrong).
  useEffect(() => {
    setPendingParty(party);
    // partyKey IS party's value-identity; depending on `party` itself would
    // re-run this on every SearchPage render, not just ones where the party
    // changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partyKey]);

  function handlePartyChange(next: PartyValue) {
    setPendingParty(next);
    if (!isPartyComplete(next)) return;
    void navigate({
      to: '/search',
      search: { checkin, checkout, ...partyToSearch(next) },
      replace: true,
    });
  }

  // The picker fires onChange on the FIRST click too (check-in chosen,
  // check-out not yet). Writing that straight to the URL flips `hasDates`
  // false in SearchPage, which unmounts this whole component — and the open
  // picker with it — mid-selection. So the range lives here and only
  // reaches the URL once a full pair is picked.
  const [pendingRange, setPendingRange] = useState<DateRangeValue>({ checkin, checkout });

  // Back/forward changes checkin/checkout via the URL without ever going
  // through handleRangeChange below — this keeps pendingRange in sync with
  // that case too, not just this component's own navigate() calls.
  useEffect(() => {
    setPendingRange({ checkin, checkout });
  }, [checkin, checkout]);

  function handleRangeChange(next: DateRangeValue) {
    setPendingRange(next);
    if (next.checkin && next.checkout) {
      void navigate({
        to: '/search',
        search: { checkin: next.checkin, checkout: next.checkout, ...partyToSearch(party) },
        replace: true,
      });
    }
  }

  const [sort, setSort] = useState<Sort>('recommended');
  const [amenityFilter, setAmenityFilter] = useState<Set<string>>(new Set());
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // Fix round 2 (OH-130 review, owner-approved scope change): Sort moves out
  // of the sticky bar below `sm` and into the mobile Filters row instead —
  // it was the weakest tenant of a bar that has to stay short there. Reused
  // rather than a second breakpoint of our own: MOBILE_QUERY is `(max-width:
  // 639px)`, which is exactly Tailwind's `sm` boundary (Tailwind v4 default,
  // unmodified in this project's @theme block) — so the JS gate below and
  // the `sm:` classes in the JSX agree on the same cutoff by construction,
  // not by two numbers kept in sync by hand.
  const isMobile = useIsMobile();

  const results = useMemo(() => data ?? [], [data]);

  // Amenities present across the result set (for the client-side filter rail).
  const allAmenities = useMemo(() => {
    const set = new Set<string>();
    for (const r of results) for (const a of r.apartment.amenities) set.add(a);
    return [...set].sort();
  }, [results]);

  const shown = useMemo(() => {
    let list = results.filter((r) =>
      [...amenityFilter].every((a) => r.apartment.amenities.includes(a)),
    );
    if (sort === 'price-low') list = [...list].sort((a, b) => a.per_night_minor - b.per_night_minor);
    else if (sort === 'price-high') list = [...list].sort((a, b) => b.per_night_minor - a.per_night_minor);
    return list;
  }, [results, amenityFilter, sort]);

  function toggleAmenity(a: string) {
    setAmenityFilter((prev) => {
      const next = new Set(prev);
      if (next.has(a)) next.delete(a);
      else next.add(a);
      return next;
    });
  }
  const clearAmenities = () => setAmenityFilter(new Set());

  const is422 = isError && error instanceof HttpError && error.status === 422;
  const hasResults = !isPending && !isError;

  // Fix round 3: filtering only makes sense against a list that's actually
  // showing (react-query keeps the PREVIOUS successful `data` around across
  // a subsequent error, so `allAmenities` can still read non-empty while
  // `isError` is true and the main content area is rendering an error
  // block instead of cards) — the Filters button AND the panel it opens
  // both stay gated on this. Sort has no such dependency: a sort order is
  // meaningful (or at worst inert) regardless of whether the fetch it
  // would reorder has landed yet, so it does NOT read `showFilters` or
  // `hasResults` at all below.
  const showFilters = hasResults && allAmenities.length > 0;

  // OH-134: `pendingParty` (not `party` — the URL-derived prop is complete
  // by construction, see this file's own comment on the state above) is the
  // only place an unanswered child slot is ever visible. Closing the picker
  // (Done, Escape, or an outside click — GuestPicker.tsx) never clears that
  // slot and never navigates: the guest's "2 adults · 1 child" trigger label
  // then contradicts the URL/API/results, which know nothing about a child
  // with no age. Gated on this count directly, NOT on `!isPartyComplete`
  // (review round 1): isPartyComplete also fails on `adults < MIN_ADULTS`,
  // a condition this notice's copy says nothing about — using it as the
  // gate would silently show the (wrong) plural child-age copy in a
  // hypothetical adults-only-incomplete state, correct today only because
  // validateSearch and GuestPicker's own decrement floor happen to make
  // that state unreachable elsewhere. Keying on the count this copy is
  // actually about needs no such invariant from other files.
  const unansweredChildCount = pendingParty.childrenAges.filter((age) => age === null).length;

  return (
    <main className="flex-1">
      {/* Summary bar */}
      <div className="sticky top-0 z-20 border-b border-[var(--border-hairline)] bg-[rgba(250,250,248,0.92)] backdrop-blur-[10px]">
        <div className="mx-auto flex w-full max-w-6xl flex-wrap items-center gap-4 px-6 py-3">
          <div className="flex items-center gap-2 rounded-full border border-border bg-[var(--surface-sunken)] px-4 py-2 text-sm">
            <SlidersHorizontal className="h-4 w-4 text-[var(--ollio-navy)]" aria-hidden="true" />
            <span className="font-semibold text-[var(--ollio-ink)]">Belgrade</span>
          </div>
          {/* Fix round 2: stayed a single (never-wrapping, never-stacking)
              row this time — round 1's `flex-col` stack fixed the overflow
              but cost 170px of sticky-bar height on a phone (three ~50px
              rows), ~37% of a 667px iPhone SE screen sitting permanently
              above the results the guest came to look at. Below `sm` this
              group now holds only Dates + Guests (Sort moved to the mobile
              Filters row below — owner-approved, see that block's own
              comment) and lets THOSE TWO shrink instead: `min-w-0 flex-1`
              splits the bar's available width between them, overridden back
              to the original fixed `min-w-[160px]` floor (`sm:flex-initial`
              restores flex's own default `0 1 auto`, i.e. no grow, DOES
              shrink, auto basis — exactly what an utterly unstyled flex
              item already does, so `sm:` and up renders byte-identical to
              before this round) once there is room for three fixed-width
              controls again.
              `min-w-0` is not decorative: DateRangePicker's trigger has a
              `truncate` span (its own file), but a flex item's default
              `min-width: auto` resolves to a CONTENT-based floor — without
              this, a long label (a straddling-year range like "30 Dec 2026
              – 2 Jan 2027") stops the item from shrinking at all and pushes
              the row wider instead of clipping. Identical reasoning to
              `routes/index.tsx`'s own `min-w-0` on this same component,
              see its comment there for the full mechanism. */}
          <div className="flex w-full items-center gap-3 sm:ml-auto sm:w-auto">
            {!isPending && !isError ? (
              <span className="hidden text-sm text-muted sm:inline">
                {shown.length} {shown.length === 1 ? 'home' : 'homes'}
              </span>
            ) : null}
            <div className="min-w-0 flex-1 sm:min-w-[160px] sm:flex-initial">
              {/* No dayData: no apartment is picked yet on the results page,
                  same as the home search bar (docs/27 §U1) — this stays a
                  plain future-date picker, not one priced/restricted against
                  any one listing's calendar. */}
              <DateRangePicker
                value={pendingRange}
                onChange={handleRangeChange}
                presentation="field"
              />
            </div>
            <div className="min-w-0 flex-1 sm:min-w-[160px] sm:flex-initial">
              <GuestPicker value={pendingParty} onChange={handlePartyChange} />
            </div>
            {/* Sort's home here is `sm` and up ONLY — below that it renders
                in the mobile Filters row instead (see that block). Gated in
                JS (`isMobile`), not CSS (`sm:hidden`): two simultaneously
                mounted `aria-label="Sort"` controls would be an ambiguous
                a11y target (and vitest's `css:false` config means a CSS-only
                `hidden` class has no effect in tests at all — a real second
                control, not just a visually-hidden one, would break every
                `getByRole('combobox', {name: /sort/i})` query in this
                file). */}
            {!isMobile ? (
              <div className="sm:min-w-[160px]">
                <Select
                  aria-label="Sort"
                  value={sort}
                  onChange={(e) => setSort(e.target.value as Sort)}
                  options={[
                    { value: 'recommended', label: 'Recommended' },
                    { value: 'price-low', label: 'Price: Low to High' },
                    { value: 'price-high', label: 'Price: High to Low' },
                  ]}
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="mx-auto flex w-full max-w-6xl gap-9 px-6 py-8">
        {/* Desktop filter rail (amenities — client-side, sticky on scroll). */}
        {hasResults && allAmenities.length > 0 ? (
          <aside className="hidden w-56 flex-none self-start lg:sticky lg:top-20 lg:block">
            <AmenityFilter
              amenities={allAmenities}
              selected={amenityFilter}
              onToggle={toggleAmenity}
              onClear={amenityFilter.size > 0 ? clearAmenities : undefined}
            />
          </aside>
        ) : null}

        <div className="min-w-0 flex-1">
          <header className="mb-6">
            <h1 className="font-serif text-3xl font-semibold tracking-tight text-[var(--ollio-ink)] sm:text-4xl">
              Apartments in Belgrade
            </h1>
            {hasResults && results.length > 0 ? (
              <p className="mt-1 text-muted">Curated homes, all looked after by our local team.</p>
            ) : null}
          </header>

          {/* Mobile Filters + Sort row (the rail above is desktop-only).
              Fix round 2 (owner-approved): Sort's mobile home moved here
              from the sticky bar, which stays two controls (Dates, Guests)
              below `sm` so it doesn't grow tall on a phone.

              Fix round 3: this row (and Sort specifically) must not blink
              out during the exact interaction this task adds — changing
              dates flips the query key, `isPending` goes true, and the OLD
              gate (`hasResults && ...`) unmounted the whole row, taking
              Sort with it, until the new results landed. The desktop
              sticky-bar Sort never had this problem (it only ever read
              `isMobile`); this brings the mobile copy in line with it. The
              row now renders whenever `isMobile` is true, full stop — NOT
              only when `showFilters` is also true, the way it's still
              gated below `sm` up through round 2. Filters (button AND its
              panel) keeps the `showFilters` gate: unlike Sort, a filter
              control is genuinely meaningless — not just idle — when
              there's no list to filter. */}
          {isMobile || showFilters ? (
            <div className="mb-5 lg:hidden">
              <div className="flex items-center gap-3">
                {showFilters ? (
                  <button
                    type="button"
                    onClick={() => setMobileFiltersOpen((o) => !o)}
                    aria-expanded={mobileFiltersOpen}
                    className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm font-semibold text-[var(--ollio-ink)]"
                  >
                    <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />
                    Filters{amenityFilter.size > 0 ? ` (${amenityFilter.size})` : ''}
                  </button>
                ) : null}
                {isMobile ? (
                  <div className="min-w-0 flex-1">
                    <Select
                      aria-label="Sort"
                      value={sort}
                      onChange={(e) => setSort(e.target.value as Sort)}
                      options={[
                        { value: 'recommended', label: 'Recommended' },
                        { value: 'price-low', label: 'Price: Low to High' },
                        { value: 'price-high', label: 'Price: High to Low' },
                      ]}
                    />
                  </div>
                ) : null}
              </div>
              {/* Same `showFilters` gate as the button that opens it: a
                  refetch (or an error) that drops `showFilters` to false
                  must close this along with the button, not leave a panel
                  open with no way to reach the toggle that closes it —
                  `mobileFiltersOpen` itself is untouched by that refetch
                  (it's a separate `useState`, and nothing here resets it),
                  so this gate is the only thing standing between a stale
                  `true` and an orphaned panel. */}
              {mobileFiltersOpen && showFilters ? (
                <div className="mt-3 rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card p-4">
                  <AmenityFilter
                    amenities={allAmenities}
                    selected={amenityFilter}
                    onToggle={toggleAmenity}
                    onClear={amenityFilter.size > 0 ? clearAmenities : undefined}
                  />
                </div>
              ) : null}
            </div>
          ) : null}

          {/* OH-134: lives here, ABOVE the results grid, not inside the
              sticky summary bar above — the owner's call, to avoid growing
              that bar (it's already been walked back once from 249px to
              132px, see the sticky-bar comment up top) and because a notice
              qualifying "these results" reads better sitting next to them.
              `role="status"` is this codebase's established idiom for a
              notice that appears without the guest acting on the results
              area itself (same pattern as BookingWidget.tsx's own race
              notice) — it carries an implicit `aria-live="polite"`, so a
              screen-reader guest is told even though nothing here moved
              focus. */}
          {unansweredChildCount > 0 ? (
            <p
              role="status"
              className="mb-4 rounded-[var(--radius-control)] border border-[var(--ollio-gold)] bg-[rgba(197,165,90,0.12)] px-3 py-2 text-sm text-[var(--ollio-gold-deep)]"
            >
              {unansweredChildCount === 1
                ? m.search_child_age_notice_one()
                : m.search_child_age_notice_other()}
            </p>
          ) : null}

          {isPending ? (
            <SearchSkeleton />
          ) : is422 ? (
            <StatusBlock title="Those dates don't work" body="Please pick a check-out date after your check-in." />
          ) : isError ? (
            <StatusBlock title="Something went wrong" body="We couldn't run that search. Please try again." />
          ) : shown.length === 0 ? (
            <EmptyResults filtered={results.length > 0} onClear={clearAmenities} />
          ) : (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {shown.map((r: SearchResult) => (
                <ApartmentCard
                  key={r.apartment.id}
                  apt={r.apartment}
                  perNightMinor={r.per_night_minor}
              search={{ checkin, checkout, ...partyToSearch(party) }}
                  currency={r.apartment.currency}
                  // Narrower than the home grid at every desktop width: this
                  // grid shares its lg:block filter rail (w-56 = 224px) and
                  // the gap-9 (36px) next to it, both absent from the home
                  // grid ApartmentCard's default `sizes` assumes.
                  sizes={CARD_SIZES}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </main>
  );
}

// Amenity filter group — shared by the desktop rail and the mobile disclosure.
function AmenityFilter({
  amenities,
  selected,
  onToggle,
  onClear,
}: {
  amenities: string[];
  selected: Set<string>;
  onToggle: (a: string) => void;
  onClear?: () => void;
}) {
  return (
    <>
      <div className="mb-4 flex items-center justify-between">
        {/* Plain label, not a heading — keeps the page <h1> first in the tree. */}
        <span className="text-base font-bold text-[var(--ollio-ink)]">Filters</span>
        {onClear ? (
          <button
            type="button"
            onClick={onClear}
            className="text-[13px] font-semibold text-[var(--ollio-navy)] hover:underline"
          >
            Clear
          </button>
        ) : null}
      </div>
      <div className="text-xs font-bold uppercase tracking-[0.08em] text-[var(--ollio-gold-deep)]">
        Amenities
      </div>
      <div className="mt-3 flex flex-col gap-2.5">
        {amenities.map((a) => (
          <Checkbox
            key={a}
            label={formatAmenity(a)}
            checked={selected.has(a)}
            onChange={() => onToggle(a)}
          />
        ))}
      </div>
    </>
  );
}

// Empty state — warm panel + calendar icon. `filtered` distinguishes "filters
// hid everything" (offer Clear) from "no availability for these dates".
function EmptyResults({ filtered, onClear }: { filtered: boolean; onClear: () => void }) {
  return (
    <div className="rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-[var(--surface-warm)] px-6 py-16 text-center">
      <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-card shadow-[var(--shadow-sm)]">
        <CalendarX2 className="h-7 w-7 text-[var(--ollio-gold-deep)]" aria-hidden="true" />
      </div>
      <h2 className="mb-2 font-serif text-2xl font-semibold text-[var(--ollio-ink)]">
        {filtered ? 'No homes match your filters' : 'No homes for these dates'}
      </h2>
      <p className="mx-auto mb-6 max-w-sm text-[15px] leading-relaxed text-muted">
        {filtered
          ? 'Try removing a filter to see more of the collection.'
          : "Everything's booked for your dates. Try shifting them a few days."}
      </p>
      {filtered ? (
        <button
          type="button"
          onClick={onClear}
          className="inline-flex h-10 items-center justify-center rounded-full bg-accent px-6 font-semibold text-accent-foreground shadow-[var(--shadow-gold)] transition hover:bg-[var(--accent-hover)]"
        >
          Clear filters
        </button>
      ) : (
        <Link
          to="/"
          className="inline-flex h-10 items-center justify-center rounded-full bg-accent px-6 font-semibold text-accent-foreground shadow-[var(--shadow-gold)] transition hover:bg-[var(--accent-hover)]"
        >
          Adjust dates
        </Link>
      )}
    </div>
  );
}

function StatusBlock({ title, body, children }: { title: string; body: string; children?: React.ReactNode }) {
  return (
    <div className="rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card px-6 py-14 text-center shadow-[var(--shadow-sm)]">
      <h2 className="mb-2 font-serif text-2xl font-semibold text-[var(--ollio-ink)]">{title}</h2>
      <p className="mx-auto max-w-sm text-[15px] leading-relaxed text-muted">{body}</p>
      {children}
    </div>
  );
}

function SearchSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" aria-busy="true" aria-label="Searching…">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="overflow-hidden rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card">
          <div className="aspect-[4/3] w-full animate-pulse bg-[var(--surface-sunken)]" />
          <div className="space-y-3 p-5">
            <div className="h-5 w-2/3 animate-pulse rounded bg-[var(--surface-sunken)]" />
            <div className="h-3 w-1/3 animate-pulse rounded bg-[var(--surface-sunken)]" />
            <div className="h-4 w-1/4 animate-pulse rounded bg-[var(--surface-sunken)]" />
          </div>
        </div>
      ))}
    </div>
  );
}
